import React, { memo } from 'react';
import { makeStyles, createStyles, Theme } from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';
import { timeToPixels } from 'components/Schedule/scheduleHelpers';
import {
  getJobCardBorder,
  getJobCardStyle,
  getReportedIconColor,
  getReportedSectionBorderColor
} from 'utils/jobStatus';
import { JobCardStyle } from '@material-ui/core/styles/createPalette';
import { getOpacity, MapElementType } from 'utils/mapElements';
import { CustomerAppointmentType } from 'models/Appointment';
import { ReportProblem } from '@material-ui/icons';
import GpsFixedIcon from '@material-ui/icons/GpsFixed';
import { ScheduleAssignmentType } from 'models/Schedule';
import OvoLogo from 'components/OvoLogo/OvoLogo';
import { JobCardProps, JobCardStyleType, JobCardViewMode } from './JobCard';
import EVMarker from './EVMarker';

interface TraveTimeProps {
  time: number;
  color: string;
  hide?: boolean;
  assigmentType?: ScheduleAssignmentType;
  visible: boolean;
}

type TravelTimeStyleProps = Omit<TraveTimeProps, 'visible'>;

export const assignedCardMapHeight = 32;
const assignedCardTimelineHeight = 44;
export const assignedCardMaxMapLength = 100;

const getBoxShadowColor = (jobCardStyle: JobCardStyleType, theme: Theme) => {
  switch (jobCardStyle) {
    case JobCardStyleType.IN_PROGRESS:
      return theme.palette.aegis.colour.teal.dark;
    case JobCardStyleType.BOOKED:
      return theme.palette.aegis.colour.blue.lighter;
    case JobCardStyleType.COMPLETED:
      return theme.palette.aegis.colour.green.lighter;
    case JobCardStyleType.CANCELLED:
      return theme.palette.aegis.colour.grey.darker;
    case JobCardStyleType.ABORTED:
      return theme.palette.aegis.colour.grey.darker;
    case JobCardStyleType.AT_RISK:
      return theme.palette.aegis.colour.orange.lighter;
    case JobCardStyleType.EN_ROUTE:
      return theme.palette.aegis.colour.blue.light;
    default:
      return theme.palette.aegis.colour.grey.darker;
  }
};

const getBoxShadow = (
  theme: Theme,
  viewMode: JobCardViewMode,
  jobCardStyleType: JobCardStyleType
) => {
  return `0px 0px 0px ${
    viewMode === JobCardViewMode.TIMELINE
      ? theme.spacing(0.5)
      : theme.spacing(0.75)
  }px ${alpha(getBoxShadowColor(jobCardStyleType, theme), 0.5)}`;
};

interface BoxShadowProps {
  boxShadowEnabled: boolean;
}

const useJobCardStyles = makeStyles<
  Theme,
  JobCardProps & JobCardStyle & BoxShadowProps
>(
  theme => {
    return createStyles({
      warning: {
        fontSize: theme.typography.fontSize,
        color: ({ jobCardStyleType }) => getReportedIconColor(jobCardStyleType)
      },
      warningContainer: {
        width: '100%',
        height: theme.spacing(2.125),
        display: 'flex',
        justifyContent: 'center',
        flex: 1,
        borderBottom: ({ jobCardStyleType }) =>
          `1px solid ${getReportedSectionBorderColor(jobCardStyleType)}`,
        paddingBottom: ({ viewMode }) =>
          viewMode === JobCardViewMode.MAP
            ? theme.spacing(0.7)
            : theme.spacing(0.3)
      },
      fixedJobsLogo: {
        '& path': {
          fill: theme.palette.aegis.semantic.primary.muted
        },
        width: 10,
        height: 'auto',
        marginTop: theme.spacing(0.5),
        shapeRendering: 'crispEdges'
      },
      card: {
        display: 'flex',
        flexWrap: 'wrap',
        width: ({ duration, viewMode, assigmentType }) => {
          if (viewMode === JobCardViewMode.MAP) {
            if (assigmentType !== ScheduleAssignmentType.Fixed) {
              return timeToPixels(Math.min(duration, assignedCardMaxMapLength));
            }
            return undefined;
          }
          return timeToPixels(duration);
        },
        maxWidth: ({ viewMode, assigmentType }) => {
          if (
            viewMode === JobCardViewMode.MAP &&
            assigmentType === ScheduleAssignmentType.Fixed
          ) {
            return theme.spacing(6);
          }

          return undefined;
        },
        height: ({ viewMode, halfHeight, assigmentType }) => {
          if (viewMode === JobCardViewMode.MAP) {
            if (assigmentType !== ScheduleAssignmentType.Fixed) {
              return assignedCardMapHeight;
            }
            return 'auto';
          }
          if (halfHeight) {
            return assignedCardTimelineHeight / 2;
          }
          return assignedCardTimelineHeight;
        },
        padding: ({ viewMode, assigmentType }) => {
          if (
            viewMode === JobCardViewMode.MAP &&
            assigmentType === ScheduleAssignmentType.Fixed
          ) {
            return theme.spacing(0.125, 1);
          }

          return undefined;
        },
        backgroundColor: ({ background }) =>
          background || theme.palette.common.white,
        overflow: 'hidden',
        position: 'relative',
        left: ({ minutesWaiting }) =>
          minutesWaiting ? timeToPixels(minutesWaiting) : 0,
        zIndex: 3,
        border: ({ jobCardStyleType, viewMode }) =>
          getJobCardBorder(jobCardStyleType, viewMode),
        borderRadius: '3px',
        boxSizing: 'border-box',
        opacity: ({ viewMode, selectedEngineer, engineerId }) =>
          viewMode === JobCardViewMode.TIMELINE
            ? 1
            : getOpacity(MapElementType.ASSIGNED, selectedEngineer, engineerId),
        boxShadow: ({
          boxShadowEnabled,
          appointmentId,
          engineerId,
          isStartOrEndJob,
          selectedEngineer,
          selectedJobCardDetails,
          jobCardStyleType,
          viewMode
        }) => {
          const shouldPressedStyleShow =
            boxShadowEnabled &&
            !isStartOrEndJob &&
            selectedEngineer === engineerId &&
            appointmentId === selectedJobCardDetails?.id;

          return shouldPressedStyleShow
            ? getBoxShadow(theme, viewMode, jobCardStyleType)
            : 'none';
        }
      },
      labelWrapper: {
        '$warningContainer + &': {
          height: '50%'
        },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '100%'
      },
      label: {
        ...theme.typography.micro,
        width: ({ duration }) => timeToPixels(duration),
        color: ({ color }) => color || theme.palette.common.white,
        lineHeight: ({ hasComplaints }) => (hasComplaints ? undefined : '12px'),
        textAlign: 'center',
        textTransform: 'uppercase',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        paddingLeft: theme.spacing(0.5),
        paddingRight: theme.spacing(0.5),
        zIndex: 1
      },
      evMarker: {
        position: 'absolute',
        top: 3,
        right: 3,
        width: 6,
        height: 6,
        cursor: 'pointer',
        background: theme.palette.aegis.colour.grey.darkest,
        display: 'inline-block',
        borderRadius: 50
      }
    });
  },
  { name: 'useJobCardStyles' }
);

const useTravelTimeStyles = makeStyles<Theme, TravelTimeStyleProps>(
  theme =>
    createStyles({
      travelTime: {
        backgroundColor: ({ assigmentType, color }) =>
          assigmentType === ScheduleAssignmentType.Fixed
            ? theme.palette.aegis.semantic.primary.muted
            : color,
        width: ({ time }) => `${timeToPixels(time)}px`,
        height: 18,
        opacity: ({ hide }) => (hide ? 0 : 1)
      }
    }),
  { name: 'useTravelTimeStyles' }
);

const TravelTime = (props: TraveTimeProps) => {
  const { visible, time, hide, color, assigmentType } = props;
  const classes = useTravelTimeStyles({
    time,
    hide,
    color,
    assigmentType
  });
  return visible ? (
    <div
      data-qa-id="engineerJobTravelTime"
      data-testid="engineerJobTravelTime"
      className={classes.travelTime}
    />
  ) : null;
};

const Assigned = (assignedProps: JobCardProps) => {
  const {
    jobCardStyleType,
    driveTimeTo = 0,
    driveTimeFrom = 0,
    label,
    hideTravelTime,
    hasComplaints,
    isPinned,
    assigmentType,
    viewMode
  } = assignedProps;

  const jobCardStyle = getJobCardStyle(jobCardStyleType);
  const classes = useJobCardStyles({
    ...assignedProps,
    ...jobCardStyle,
    boxShadowEnabled: true
  });

  return (
    <>
      <TravelTime
        visible
        color={jobCardStyle.background}
        time={driveTimeTo}
        hide={hideTravelTime}
        assigmentType={assigmentType}
      />
      <div
        data-qa-id="engineerJobCard"
        data-testid="engineerJobCard"
        className={classes.card}
      >
        {hasComplaints && (
          <div
            className={classes.warningContainer}
            data-testid="warningComplaints"
          >
            <ReportProblem className={classes.warning} />
          </div>
        )}
        {isPinned && (
          <div className={classes.warningContainer}>
            <GpsFixedIcon
              className={classes.warning}
              data-testid="jobCardPinnedIcon"
            />
          </div>
        )}
        {jobCardStyleType !== JobCardStyleType.NON_CUSTOMER && (
          <div className={classes.labelWrapper}>
            <div data-testid="jobCardLabel" className={classes.label}>
              {label}
            </div>
          </div>
        )}
        {(assignedProps.type === CustomerAppointmentType.EV ||
          assignedProps.type === CustomerAppointmentType.EV_ISOLATOR) && (
          <EVMarker />
        )}
        {assigmentType === ScheduleAssignmentType.Fixed &&
          (viewMode === JobCardViewMode.TIMELINE ? (
            <>
              <div className={classes.warningContainer}>
                <OvoLogo
                  srDescription="The OVO Energy logo, depicting a fixed appointment for an engineer"
                  className={classes.fixedJobsLogo}
                />
              </div>
              <div className={classes.labelWrapper}>
                <div data-testid="jobCardLabel" className={classes.label}>
                  {label}
                </div>
              </div>
            </>
          ) : (
            <div data-testid="jobCardLabel" className={classes.label}>
              {label}
            </div>
          ))}
      </div>
      {driveTimeFrom > 0 && (
        <TravelTime
          visible
          color={jobCardStyle.background}
          time={driveTimeFrom}
          hide={hideTravelTime}
          assigmentType={assigmentType}
        />
      )}
    </>
  );
};

export default memo(Assigned);
